// src/pages/trading-portal/market-data/MarketDataPage.js
// Data feed management page

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    IconButton,
    Chip,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Tooltip,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Sync as SyncIcon,
    RssFeed as RssFeedIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    DataProvider,
    DataProviderLabels,
    FeedStatus,
    FeedStatusLabels,
    FeedStatusColors,
} from '../stockTradingEnums';

const MarketDataPage = () => {
    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    // State
    const [feeds, setFeeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedFeed, setSelectedFeed] = useState(null);
    const [syncing, setSyncing] = useState(null);

    // Watchlist symbols for dropdown
    const [watchlistSymbols, setWatchlistSymbols] = useState([]);
    const [loadingSymbols, setLoadingSymbols] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        provider: '',
        symbols_tracked: [],
        refresh_interval_seconds: 60,
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-market-data', 'view');
            const add = await hasPermission('tr-market-data', 'add');
            const edit = await hasPermission('tr-market-data', 'edit');
            const del = await hasPermission('tr-market-data', 'delete');
            setCanView(view);
            setCanAdd(add);
            setCanEdit(edit);
            setCanDelete(del);
        };
        checkPermissions();
    }, []);

    // Fetch watchlist symbols for dropdown
    const fetchWatchlistSymbols = useCallback(async () => {
        setLoadingSymbols(true);
        try {
            const response = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            if (response.data.success) {
                setWatchlistSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch watchlist symbols:', err);
        } finally {
            setLoadingSymbols(false);
        }
    }, []);

    useEffect(() => {
        if (canView) fetchWatchlistSymbols();
    }, [fetchWatchlistSymbols, canView]);

    // Fetch feeds
    const fetchFeeds = useCallback(async () => {
        if (!canView) return;
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({ page: page + 1, per_page: rowsPerPage });
            const response = await axiosInstance.get(`/api/stock-trading/feeds?${params.toString()}`);

            if (response.data.success) {
                setFeeds(response.data.data || []);
                setTotalCount(response.data.pagination?.total || 0);
            } else {
                setError(response.data.message || 'Failed to fetch data feeds');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch data feeds');
        } finally {
            setLoading(false);
        }
    }, [canView, page, rowsPerPage]);

    useEffect(() => {
        if (canView) fetchFeeds();
    }, [fetchFeeds, canView]);

    // Handlers
    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };

    const resetForm = () => {
        setFormData({ name: '', provider: '', symbols_tracked: [], refresh_interval_seconds: 60 });
        setFormErrors({});
    };

    const handleCreateOpen = () => { resetForm(); setCreateDialogOpen(true); };
    const handleCreateClose = () => { setCreateDialogOpen(false); resetForm(); };

    const handleEditOpen = (feed) => {
        setSelectedFeed(feed);
        // Match existing tracked symbols to watchlist objects for autocomplete
        const trackedTickers = feed.symbols_tracked || [];
        const matchedSymbols = trackedTickers.map(ticker => {
            const found = watchlistSymbols.find(s => s.symbol === ticker);
            return found || { symbol: ticker, company_name: '' };
        });
        setFormData({
            name: feed.name || '',
            provider: feed.provider || '',
            symbols_tracked: matchedSymbols,
            refresh_interval_seconds: feed.refresh_interval_seconds || 60,
        });
        setFormErrors({});
        setEditDialogOpen(true);
    };

    const handleEditClose = () => { setEditDialogOpen(false); setSelectedFeed(null); resetForm(); };

    const handleFormChange = (field) => (event) => {
        setFormData(prev => ({ ...prev, [field]: event.target.value }));
        if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Feed name is required';
        if (!formData.provider) errors.provider = 'Provider is required';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateSubmit = async () => {
        if (!validateForm()) return;
        setSubmitting(true);

        try {
            const payload = {
                ...formData,
                symbols_tracked: formData.symbols_tracked.map(s => s.symbol),
                refresh_interval_seconds: parseInt(formData.refresh_interval_seconds, 10),
            };
            const response = await axiosInstance.post('/api/stock-trading/feeds', payload);

            if (response.data.success) {
                handleCreateClose();
                fetchFeeds();
            } else {
                setFormErrors({ submit: response.data.message || 'Failed to create feed' });
            }
        } catch (err) {
            setFormErrors({ submit: err.response?.data?.message || 'Failed to create feed' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditSubmit = async () => {
        if (!validateForm() || !selectedFeed) return;
        setSubmitting(true);

        try {
            const payload = {
                ...formData,
                symbols_tracked: formData.symbols_tracked.map(s => s.symbol),
                refresh_interval_seconds: parseInt(formData.refresh_interval_seconds, 10),
            };
            const response = await axiosInstance.put(`/api/stock-trading/feeds/${selectedFeed.id}`, payload);

            if (response.data.success) {
                handleEditClose();
                fetchFeeds();
            } else {
                setFormErrors({ submit: response.data.message || 'Failed to update feed' });
            }
        } catch (err) {
            setFormErrors({ submit: err.response?.data?.message || 'Failed to update feed' });
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (feed) => { setSelectedFeed(feed); setDeleteDialogOpen(true); };

    const handleDeleteConfirm = async () => {
        if (!selectedFeed) return;
        try {
            const response = await axiosInstance.delete(`/api/stock-trading/feeds/${selectedFeed.id}`);
            if (response.data.success) {
                fetchFeeds();
                setDeleteDialogOpen(false);
                setSelectedFeed(null);
            } else {
                setError(response.data.message || 'Failed to delete feed');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete feed');
        }
    };

    const handleSync = async (feed) => {
        setSyncing(feed.id);
        try {
            await axiosInstance.post(`/api/stock-trading/feeds/${feed.id}/sync`);
            fetchFeeds();
        } catch (err) {
            setError(err.response?.data?.message || 'Sync failed');
        } finally {
            setSyncing(null);
        }
    };

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view market data.</Alert>
            </Box>
        );
    }

    const renderFormFields = () => (
        <Grid container spacing={2} sx={{ mt: 1 }}>
            {formErrors.submit && (
                <Grid item xs={12}>
                    <Alert severity="error">{formErrors.submit}</Alert>
                </Grid>
            )}
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Feed Name"
                    value={formData.name}
                    onChange={handleFormChange('name')}
                    error={!!formErrors.name}
                    helperText={formErrors.name}
                    required
                />
            </Grid>
            <Grid item xs={12}>
                <FormControl fullWidth error={!!formErrors.provider} required>
                    <InputLabel>Provider</InputLabel>
                    <Select value={formData.provider} label="Provider" onChange={handleFormChange('provider')}>
                        {Object.values(DataProvider).map(prov => (
                            <MenuItem key={prov} value={prov}>{DataProviderLabels[prov]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    options={watchlistSymbols}
                    getOptionLabel={(option) => option.symbol ? `${option.symbol} - ${option.company_name || ''}` : ''}
                    value={formData.symbols_tracked}
                    onChange={(e, newValue) => {
                        setFormData(prev => ({ ...prev, symbols_tracked: newValue }));
                    }}
                    loading={loadingSymbols}
                    isOptionEqualToValue={(option, value) => option.symbol === value.symbol}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                label={option.symbol}
                                size="small"
                                {...getTagProps({ index })}
                                key={option.symbol}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Symbols"
                            placeholder="Select symbols from watchlist"
                            helperText="Choose symbols from your watchlist"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loadingSymbols ? <CircularProgress size={18} /> : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="Refresh Interval (seconds)"
                    type="number"
                    value={formData.refresh_interval_seconds}
                    onChange={handleFormChange('refresh_interval_seconds')}
                    inputProps={{ min: 10 }}
                />
            </Grid>
        </Grid>
    );

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h4" component="h1">Market Data Feeds</Typography>
                    <Chip label={totalCount} color="primary" size="small" />
                </Box>
                {canAdd && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen}
                        sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                        Create Feed
                    </Button>
                )}
            </Box>

            {/* Error */}
            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Feeds Table */}
            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Provider</TableCell>
                                <TableCell>Symbols</TableCell>
                                <TableCell>Refresh Interval</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Last Sync</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell>
                                </TableRow>
                            ) : feeds.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <RssFeedIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                        <Typography color="text.secondary">No data feeds configured</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                feeds.map((feed) => (
                                    <TableRow key={feed.id} hover>
                                        <TableCell>
                                            <Typography fontWeight="medium">{feed.name}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={DataProviderLabels[feed.provider] || feed.provider} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {Array.isArray(feed.symbols_tracked) ? feed.symbols_tracked.slice(0, 3).join(', ') : '-'}
                                                {Array.isArray(feed.symbols_tracked) && feed.symbols_tracked.length > 3 && ` +${feed.symbols_tracked.length - 3} more`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{feed.refresh_interval_seconds ? `${feed.refresh_interval_seconds}s` : '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={FeedStatusLabels[feed.status] || feed.status || 'Unknown'}
                                                size="small"
                                                color={FeedStatusColors[feed.status] || 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {feed.last_sync_at ? new Date(feed.last_sync_at).toLocaleString() : 'Never'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="Sync Now">
                                                    <IconButton size="small" onClick={() => handleSync(feed)} disabled={syncing === feed.id}>
                                                        {syncing === feed.id ? <CircularProgress size={18} /> : <SyncIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                                {canEdit && (
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleEditOpen(feed)}><EditIcon /></IconButton>
                                                    </Tooltip>
                                                )}
                                                {canDelete && (
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" color="error" onClick={() => handleDeleteClick(feed)}><DeleteIcon /></IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Paper>

            {/* Create Feed Dialog */}
            <Dialog open={createDialogOpen} onClose={handleCreateClose} maxWidth="sm" fullWidth>
                <DialogTitle>Create Data Feed</DialogTitle>
                <DialogContent>{renderFormFields()}</DialogContent>
                <DialogActions>
                    <Button onClick={handleCreateClose} disabled={submitting}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateSubmit} disabled={submitting}
                        sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                        {submitting ? <CircularProgress size={24} /> : 'Create Feed'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Feed Dialog */}
            <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Feed: {selectedFeed?.name}</DialogTitle>
                <DialogContent>{renderFormFields()}</DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose} disabled={submitting}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditSubmit} disabled={submitting}
                        sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}>
                        {submitting ? <CircularProgress size={24} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Data Feed</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{selectedFeed?.name}</strong>?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MarketDataPage;
